
import { useState, useRef, useEffect } from "react";
import { Navbar } from "@/components/Navbar";
import { SendHorizonal, Bot, User, Mic, AlertCircle, Coins, MoreVertical, Edit, Trash2, Plus, List } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Avatar } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Progress } from "@/components/ui/progress";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "@/hooks/use-toast";
import { useVitaChat, ChatMessage, ChatSession } from "@/hooks/use-vita-chat";

export default function VitaChat() {
  const [input, setInput] = useState("");
  const [showChatList, setShowChatList] = useState(true);
  const [editChatTitle, setEditChatTitle] = useState<{ id: string, title: string } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [showThinking, setShowThinking] = useState(false);
  
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const {
    messages,
    isLoading,
    error,
    chats,
    currentChatId,
    credits,
    sendMessage,
    fetchMessages,
    createNewChat,
    updateChatTitle,
    deleteChat
  } = useVitaChat();

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, showThinking]);

  // Focus input on load
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [currentChatId]);

  // Redirect if not logged in
  useEffect(() => {
    if (!user) {
      navigate('/auth');
    }
  }, [user, navigate]);

  const handleSendMessage = async () => {
    if (!input.trim() || isLoading || !user) return;
    
    const trimmedInput = input.trim();
    setInput("");
    
    // Show thinking indicator
    setShowThinking(true);
    
    try {
      await sendMessage(trimmedInput);
    } finally {
      // Hide thinking indicator when done
      setShowThinking(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleNewChat = async () => {
    await createNewChat();
  };

  const handleChatSelect = async (chatId: string) => {
    await fetchMessages(chatId);
    // On mobile, close the chat list after selection
    if (window.innerWidth < 768) {
      setShowChatList(false);
    }
  };

  const handleSaveTitle = async () => {
    if (editChatTitle) {
      await updateChatTitle(editChatTitle.id, editChatTitle.title);
      setEditChatTitle(null);
    }
  };

  const handleDeleteConfirm = async () => {
    if (confirmDelete) {
      await deleteChat(confirmDelete);
      setConfirmDelete(null);
    }
  };

  // Empty state welcome message
  const emptyStateMessage: ChatMessage = {
    id: "welcome",
    content: "Hello! I'm Vita, your AI wellness assistant. How can I help you with your wellness journey today? I can provide guidance on nutrition, fitness, supplements, and mindfulness practices.",
    role: "assistant",
    timestamp: new Date()
  };

  // Show this message if no messages in current chat
  const displayMessages = messages.length > 0 ? messages : [emptyStateMessage];

  return (
    <>
      <Navbar />
      <div className="container mx-auto py-6 px-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {/* Chat List - Hidden on mobile by default */}
          <div className={cn(
            "md:block",
            showChatList ? "block" : "hidden"
          )}>
            <Card className="h-[80vh] flex flex-col">
              <CardHeader className="space-y-1 pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-xl">Chats</CardTitle>
                  <Button variant="ghost" size="icon" onClick={handleNewChat}>
                    <Plus className="h-5 w-5" />
                  </Button>
                </div>
                <CardDescription>Your conversations with Vita</CardDescription>
              </CardHeader>
              
              <ScrollArea className="flex-1 px-3">
                <div className="space-y-2 pb-4">
                  {chats.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">No chats yet. Start a new conversation!</p>
                  ) : (
                    chats.map((chat) => (
                      <div 
                        key={chat.id} 
                        className={cn(
                          "flex items-center justify-between p-3 rounded-md cursor-pointer hover:bg-accent group",
                          currentChatId === chat.id && "bg-accent"
                        )}
                        onClick={() => handleChatSelect(chat.id)}
                      >
                        <div className="flex items-center space-x-3 truncate">
                          <Avatar className="h-8 w-8 shrink-0">
                            <Bot className="h-4 w-4" />
                          </Avatar>
                          <div className="truncate">
                            <p className="font-medium truncate">{chat.title}</p>
                            <p className="text-xs text-muted-foreground truncate">
                              {new Date(chat.updated_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              className="h-8 w-8 opacity-0 group-hover:opacity-100"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={(e) => {
                              e.stopPropagation();
                              setEditChatTitle({ id: chat.id, title: chat.title });
                            }}>
                              <Edit className="h-4 w-4 mr-2" /> Rename
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              className="text-destructive"
                              onClick={(e) => {
                                e.stopPropagation();
                                setConfirmDelete(chat.id);
                              }}
                            >
                              <Trash2 className="h-4 w-4 mr-2" /> Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    ))
                  )}
                </div>
              </ScrollArea>
              
              {/* Credits Display */}
              <div className="p-4 border-t">
                {credits && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Credits</span>
                      <span className="text-sm font-medium">{credits.used}/{credits.limit}</span>
                    </div>
                    <Progress value={(credits.used / credits.limit) * 100} className="h-1" />
                  </div>
                )}
              </div>
            </Card>
          </div>
          
          {/* Chat Area */}
          <div className="md:col-span-3">
            <div className="bg-card border rounded-lg shadow-sm overflow-hidden flex flex-col h-[80vh]">
              <div className="p-4 border-b flex items-center justify-between">
                <div className="flex items-center">
                  {/* Mobile Chat List Toggle */}
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="md:hidden mr-2"
                    onClick={() => setShowChatList(!showChatList)}
                  >
                    <List className="h-5 w-5" />
                  </Button>
                  
                  <Avatar className="h-8 w-8 mr-2">
                    <Bot className="h-4 w-4" />
                  </Avatar>
                  <div>
                    <h2 className="text-lg font-semibold">Vita</h2>
                    <Badge variant="outline" className="text-xs font-normal text-emerald-600 bg-emerald-50">
                      AI Wellness Assistant
                    </Badge>
                  </div>
                </div>
                
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="ml-auto"
                  onClick={handleNewChat}
                >
                  <Plus className="h-4 w-4 mr-1" /> New Chat
                </Button>
              </div>
              
              <div className="flex-1 overflow-y-auto p-6 bg-background/50">
                {error && (
                  <Alert variant="destructive" className="mb-4">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Error</AlertTitle>
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}
                
                <div className="space-y-6">
                  {displayMessages.map((message) => (
                    <div
                      key={message.id}
                      className={cn(
                        "flex items-start space-x-3 max-w-3xl",
                        message.role === "user" ? "ml-auto" : ""
                      )}
                    >
                      {message.role === "assistant" && (
                        <Avatar className="h-8 w-8 mt-1">
                          <Bot className="h-4 w-4" />
                        </Avatar>
                      )}
                      <div
                        className={cn(
                          "p-4 rounded-lg",
                          message.role === "user"
                            ? "bg-primary text-primary-foreground"
                            : "bg-secondary"
                        )}
                      >
                        <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                        <p className="text-xs opacity-70 mt-2">
                          {message.timestamp.toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </p>
                      </div>
                      {message.role === "user" && (
                        <Avatar className="h-8 w-8 mt-1">
                          <User className="h-4 w-4" />
                        </Avatar>
                      )}
                    </div>
                  ))}
                  
                  {/* Thinking indicator */}
                  {showThinking && (
                    <div className="flex items-start space-x-3 max-w-3xl">
                      <Avatar className="h-8 w-8 mt-1">
                        <Bot className="h-4 w-4" />
                      </Avatar>
                      <div className="p-4 rounded-lg bg-secondary">
                        <div className="flex items-center space-x-2">
                          <div className="h-2 w-2 bg-primary rounded-full animate-pulse"></div>
                          <div className="h-2 w-2 bg-primary rounded-full animate-pulse delay-150"></div>
                          <div className="h-2 w-2 bg-primary rounded-full animate-pulse delay-300"></div>
                          <span className="text-sm ml-1">Vita is thinking...</span>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div ref={messagesEndRef} />
                </div>
              </div>
              
              <div className="p-4 border-t bg-card">
                <div className="flex items-end">
                  <Button variant="ghost" size="icon" className="h-10 w-10 rounded-full mr-2">
                    <Mic className="h-5 w-5" />
                  </Button>
                  <div className="flex-1 relative">
                    <Textarea
                      ref={inputRef}
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder="Ask Vita about wellness, nutrition, fitness..."
                      className="min-h-[60px] resize-none border border-input focus-visible:ring-1 pr-10"
                      rows={2}
                      disabled={isLoading || showThinking}
                    />
                    {isLoading && !showThinking && (
                      <div className="absolute right-3 top-[50%] transform -translate-y-1/2">
                        <div className="h-5 w-5 border-2 border-t-primary rounded-full animate-spin"></div>
                      </div>
                    )}
                  </div>
                  <Button
                    className="ml-2 p-2 h-12 w-12 rounded-full"
                    disabled={!input.trim() || isLoading || showThinking}
                    onClick={handleSendMessage}
                  >
                    <SendHorizonal className="h-5 w-5" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Edit Chat Title Dialog */}
      <Dialog open={!!editChatTitle} onOpenChange={(open) => !open && setEditChatTitle(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename Chat</DialogTitle>
            <DialogDescription>
              Give your chat a more descriptive name to help you find it later.
            </DialogDescription>
          </DialogHeader>
          <Input 
            value={editChatTitle?.title || ''}
            onChange={(e) => setEditChatTitle(prev => prev ? { ...prev, title: e.target.value } : null)}
            placeholder="Chat title"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditChatTitle(null)}>Cancel</Button>
            <Button onClick={handleSaveTitle}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={!!confirmDelete} onOpenChange={(open) => !open && setConfirmDelete(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Chat</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this chat? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteConfirm}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
